import { z } from 'zod';
import { id } from '@quizee/events';
import { domainSchema, MAX_DOMAINS, normaliseDomains } from './brand';
import { consentSegmentsSchema, parseConsentText } from './consent';
import {
  interactionContentSchema,
  presentationSchema,
  type NewsletterSignup,
} from './content';
import { checkFormatRules, FORMAT_RULES } from './formats';
import type { RenderableDocument } from './rendering';
import { MAX_SHARED_BRANDS, resultsScopeSchema } from './sharing';

export const PUBLISHED_SCHEMA_VERSION = 1 as const;

/**
 * How many domains one document may carry.
 *
 * The union of every brand's list, so the ceiling is the shared brand limit multiplied by the per
 * brand one. A document anywhere near it would be tens of kilobytes fetched on every page view,
 * which is a reason to look at the document shape again rather than a size anybody will reach: a
 * masthead has one or two domains and a staging one.
 */
export const PUBLISHED_DOMAINS_MAX = MAX_DOMAINS * MAX_SHARED_BRANDS;

/**
 * The newsletter signup as a reader's browser receives it.
 *
 * Consent travels as segments rather than markup for the same reason the interaction's own notice
 * does: the widget renders into a shadow root on somebody's live article and must never be handed
 * HTML to interpret.
 *
 * `double_opt_in` is only a fallback. The widget says what happens next based on the status the
 * subscriptions endpoint returns, and reads this only when a 2xx comes back unreadable.
 */
export const publishedSignupSchema = z
  .object({
    enabled: z.boolean(),
    heading: z.string().max(200).optional(),
    button_label: z.string().max(60).optional(),
    consent: consentSegmentsSchema,
    double_opt_in: z.boolean(),
  })
  .strict();

export type PublishedSignup = z.infer<typeof publishedSignupSchema>;

/**
 * The signup block a document carries, or nothing at all.
 *
 * Three ways to get nothing, and they are one answer rather than three: no signup configured, a
 * signup switched off, and a signup whose wording came out empty. The last is not defensive
 * tidying. Asking for marketing consent means showing a reader what they are agreeing to and
 * recording those exact words, so with nothing to show there is nothing that could honestly be
 * recorded, and leaving the step out is the only honest answer (D30). The widget already decides
 * it that way, so deciding it here as well means the document and the screen agree, and the
 * subscriptions endpoint can refuse exactly what the widget would never have offered.
 *
 * The wording is expected to be resolved already: `resolveSignup` fills in the brand's or the org's
 * before this is called, and a version frozen without it is treated as having none.
 */
export function publishedSignup(
  signup: NewsletterSignup | undefined,
  doubleOptIn: boolean,
): PublishedSignup | undefined {
  if (signup === undefined || !signup.enabled) return undefined;

  const consent = parseConsentText(signup.consent_text ?? '');
  if (consent.length === 0) return undefined;

  return publishedSignupSchema.parse({
    enabled: true,
    ...(signup.heading === undefined ? {} : { heading: signup.heading }),
    ...(signup.button_label === undefined ? {} : { button_label: signup.button_label }),
    consent,
    double_opt_in: doubleOptIn,
  });
}

/**
 * One further brand this version was published for, carrying everything that brand needs.
 *
 * The owning brand is not in here. It stays where it has always been, at the top of the document,
 * so an interaction on one brand produces exactly the document it produced before sharing existed
 * and a widget that has never heard of sharing goes on reading it. This list is the brands beyond
 * that one, so it is absent in the common case rather than being a list of one.
 *
 * The presentation is fully resolved rather than a difference from the owning brand's. A
 * difference would be smaller on the wire, but it would put a merge back in the widget, which is
 * the thing D26 took out: a colour a reader sees would be decided in two places, and the two would
 * eventually disagree on somebody's live article.
 */
export const publishedBrandSchema = z
  .object({
    brand_id: id('brand'),
    presentation: presentationSchema,
    /** This brand's own notice, frozen at publish exactly like the owning brand's. */
    consent: z
      .object({ segments: consentSegmentsSchema })
      .strict()
      .optional(),
    /**
     * This brand's results file, present only when results are scoped per brand.
     *
     * Carried rather than derived for the same reason as `endpoints` below: a widget built a year
     * ago must not have to guess a url by string surgery. When results are combined this is absent
     * and every brand reads the one file in `endpoints.results`.
     */
    results: z.string().url().optional(),
  })
  .strict();

export type PublishedBrand = z.infer<typeof publishedBrandSchema>;

/**
 * The immutable document the widget fetches from the CDN.
 *
 * It is a published version, not a live view of the draft: publishing writes one of these to S3 at
 * a versioned path and moves a pointer. That is what lets the read path avoid Lambda and DynamoDB
 * entirely, and what makes a publish atomic from the reader's point of view.
 *
 * Endpoints are carried in the document rather than compiled into the widget, so a widget built a
 * year ago still talks to the right place.
 */
export const publishedInteractionSchema = interactionContentSchema
  .extend({
    schema_version: z.literal(PUBLISHED_SCHEMA_VERSION),
    interaction_id: id('interaction'),
    version: z.number().int().min(1),
    org_id: id('org'),
    /**
     * The brand that owns the interaction, and the one a reader gets when the embed says nothing.
     *
     * Still exactly one brand, still required. A shared interaction adds `shared_brands` beside
     * it rather than turning this into a list, so every document already on the CDN goes on
     * parsing and every widget already on a page goes on rendering.
     */
    brand_id: id('brand'),
    published_at: z.string(),
    /**
     * The optional signup step on the completion screen.
     *
     * Absent means no step, and that is the only way to say so: a switched off signup is left out
     * rather than written as `enabled: false`, so the widget has one path for it and every document
     * published before the block existed already takes that path. See `publishedSignup`.
     */
    signup: publishedSignupSchema.optional(),
    /**
     * Required here even though it is optional on a draft.
     *
     * A draft may leave it out to mean "use the brand's theme", and publishing resolves that. The
     * document a reader's browser fetches has no brand to ask, so by the time it is written the
     * answer is already in it.
     */
    presentation: presentationSchema,
    /**
     * The consent notice, as data rather than as markup.
     *
     * Segments, never HTML: the widget renders into a shadow root on a live article under a 30KB
     * budget, where it can afford neither `innerHTML` nor a sanitiser to make `innerHTML` safe. The
     * text is sanitised on save and converted here, so the only thing that ever crosses to a reader
     * is a list of strings and hrefs.
     *
     * Optional because documents published before consent text existed do not carry it, and those
     * documents are cached, still live and still have to work.
     */
    consent: z
      .object({ segments: consentSegmentsSchema })
      .strict()
      .optional(),
    /**
     * The other brands this version may render on, each with its own resolved look and notice.
     *
     * Absent when the interaction runs on its owning brand only, which is the common case and the
     * only case that existed before sharing, so nothing that was small has grown.
     *
     * The list is what was true at publish time. The share rule behind it is dynamic (`all` means
     * whatever titles the org has now), so a brand created since is missing from here until the
     * interaction is republished, and `sharedBrandsChanged` is what tells an editor that.
     */
    shared_brands: z
      .array(publishedBrandSchema)
      .min(1)
      .max(MAX_SHARED_BRANDS - 1)
      .optional(),
    /**
     * What a reader is shown: the whole group's numbers, or their own title's.
     *
     * Absent means combined, which is what a single brand document has always shown and what a
     * document published before this existed means. Combined counts responses across titles and
     * not people: a reader of two of the group's titles is two devices and two readers, because
     * device identity is first party to each publisher's domain (D7).
     */
    results_scope: resultsScopeSchema.optional(),
    /**
     * Every domain this version may render on: the owning brand's, and every shared brand's.
     *
     * One flat union rather than a list per brand. The only question the widget has to answer is
     * "may I render on this page", and it has to answer it on somebody's live article before
     * anything else happens, so resolving which brand it is rendering as first would be a second
     * thing to get wrong there. Which brand a page paints as is a separate question, answered by
     * `renderingForBrand` from the embed's own attribute (D29), and it is cosmetic; this is not.
     *
     * The list is public, because the document is public: anybody who fetches an interaction can
     * read every domain its publisher runs it on. Those are public websites, so it is accepted.
     * A domain that is not public knowledge would be a different matter, and the case to watch is
     * a staging or pre launch masthead sitting in a brand's list beside its live one.
     *
     * Optional, and absent means render anywhere, which is what every document already on the CDN
     * carries: they were published before this field existed, they are cached and live, and
     * reading "no list" as "nowhere" would take working embeds off real articles. Same shape of
     * decision as `originAllowed`'s empty list (D28), and closed the same way, at publish.
     */
    domains: z.array(domainSchema).max(PUBLISHED_DOMAINS_MAX).optional(),
    endpoints: z
      .object({
        events: z.string().url(),
        results: z.string().url().optional(),
        /**
         * Where free text answers go. Carried rather than derived, because the widget was
         * previously reconstructing this url by string surgery on the events one, and D4 says
         * endpoints travel in the document precisely so a widget built a year ago still works.
         */
        submissions: z.string().url().optional(),
        /** Where a newsletter signup goes. Carried for the same reason as the others. */
        subscriptions: z.string().url().optional(),
      })
      .strict(),
  })
  .strict()
  /**
   * The published document is held to its format's rules, not just to the content shape.
   *
   * Without this a survey could be published with results turned on, or a trivia question with no
   * correct answer, and the first thing to discover it would be a reader.
   */
  .superRefine(checkFormatRules)
  .superRefine(checkPublishedBrands);

export type PublishedInteraction = z.infer<typeof publishedInteractionSchema>;

/**
 * The document satisfies what the widget reads, so `renderingForBrand` can be handed one directly.
 *
 * A compile time check rather than a runtime one: if the document shape drifts away from the
 * rendering contract, this stops building rather than producing a widget that looks up a brand
 * that is no longer there.
 */
type Assert<T extends true> = T;
type _NoRenderingDrift = Assert<PublishedInteraction extends RenderableDocument ? true : false>;

/**
 * Rules about the brands a document was published for.
 *
 * These are the invariants the widget assumes when it looks a brand up: one entry per brand, the
 * owning brand never repeated inside the list, and a per brand results file wherever per brand
 * results were promised. A document breaking any of them is one a reader would be served, and the
 * widget would then have to guess which of two entries is the masthead in front of them.
 */
function checkPublishedBrands(
  document: {
    format: z.infer<typeof interactionContentSchema>['format'];
    brand_id: string;
    shared_brands?: readonly PublishedBrand[] | undefined;
    results_scope?: z.infer<typeof resultsScopeSchema> | undefined;
  },
  ctx: z.RefinementCtx,
): void {
  const shared = document.shared_brands ?? [];
  const seen = new Set<string>();

  shared.forEach((entry, index) => {
    const at = ['shared_brands', index, 'brand_id'] as const;

    if (entry.brand_id === document.brand_id) {
      ctx.addIssue({
        code: 'custom',
        message: 'the owning brand is carried at the top of the document, not in shared_brands',
        path: [...at],
      });
    }
    if (seen.has(entry.brand_id)) {
      ctx.addIssue({ code: 'custom', message: 'duplicate brand', path: [...at] });
    }
    seen.add(entry.brand_id);
  });

  if (document.results_scope === undefined) return;

  if (!FORMAT_RULES[document.format].hasResults) {
    ctx.addIssue({
      code: 'custom',
      message: `a ${FORMAT_RULES[document.format].label.toLowerCase()} shows a reader no results, so there is nothing to scope`,
      path: ['results_scope'],
    });
    return;
  }

  if (document.results_scope === 'per_brand') {
    if (shared.length === 0) {
      ctx.addIssue({
        code: 'custom',
        message: 'per brand results need more than one brand to be able to see this',
        path: ['results_scope'],
      });
    }
    shared.forEach((entry, index) => {
      if (entry.results === undefined) {
        ctx.addIssue({
          code: 'custom',
          message: 'per brand results need this brand to carry its own results file',
          path: ['shared_brands', index, 'results'],
        });
      }
    });
  }
}

/**
 * The domains a published document carries, from the brands it renders on.
 *
 * The caller hands over the owning brand's list followed by every shared brand's, and this is
 * where they become one set: deduplicated, in the order they arrived, because the widget asks one
 * question of the whole union and the order is only what a person reads in the file.
 *
 * An entry that cannot be read as a domain takes the whole field away rather than being dropped
 * from it. A brand's stored list is normalised on every write, so this can only be a record
 * written before that was true, and the two ways of being wrong are not equal: a document with no
 * field renders exactly as it does today, and a document listing four of a brand's five domains
 * takes a live embed off the fifth. Nothing here may be the reason an article goes blank.
 */
export function publishedDomains(entries: readonly string[]): string[] | undefined {
  if (entries.length === 0) return undefined;

  const { domains, rejected } = normaliseDomains(entries);
  if (rejected.length > 0 || domains.length === 0) return undefined;
  return domains.slice(0, PUBLISHED_DOMAINS_MAX);
}

/** Where a published version lives on the CDN. Versioned paths are immutable and cached forever. */
export function publishedVersionKey(interactionId: string, version: number): string {
  return `i/${interactionId}/${version}.json`;
}

/** The pointer the widget actually asks for. Short cache, rewritten on every publish. */
export function publishedPointerKey(interactionId: string): string {
  return `i/${interactionId}/live.json`;
}

/**
 * Where the reader facing results document lives on the CDN.
 *
 * Beside the config it belongs to, because they are read together: the widget fetches `live.json`
 * to know what to render and `results.json` to know what to show. See D24.
 *
 * With a brand it is that brand's own file, which is what an interaction shared across titles and
 * scoped per brand needs: one file per masthead, so a reader on The Herald downloads The Herald's
 * numbers and nobody else's. Without one it is the combined file, unchanged, which is what every
 * interaction published so far uses.
 */
export function publishedResultsKey(interactionId: string, brandId?: string): string {
  return brandId === undefined
    ? `i/${interactionId}/results.json`
    : `i/${interactionId}/results-${brandId}.json`;
}

/**
 * The pointer document's cache-control, shared so the two writers cannot drift.
 *
 * `max-age=0, must-revalidate` for the browser and `s-maxage=60` for CloudFront, and both halves
 * are load bearing. See D23: the browser must ask every time, because that request is how a view
 * is counted, and a reader who opens two articles a minute apart is two views rather than one.
 * CloudFront answers the revalidation from the edge with a 304 of a few hundred bytes, so the
 * origin sees one read a minute per interaction however busy the story is.
 *
 * Versioned documents are untouched by this: they are immutable and cached for a year.
 */
export const POINTER_CACHE_CONTROL = 'public, max-age=0, must-revalidate, s-maxage=60';

/**
 * The results document's cache-control.
 *
 * Short enough to feel live on a busy story, long enough to collapse a stampede into one read.
 * The file is never invalidated: an invalidation is slow and billed, and a five second TTL buys
 * the same freshness for nothing.
 */
export const RESULTS_CACHE_CONTROL = 'public, max-age=5, stale-while-revalidate=30';

/**
 * What a reader's browser is allowed to see, and the exact shape
 * `GET /v1/interactions/:id/results` returns.
 *
 * Aggregates only: no identity, no per response detail, and nothing the editor facing results
 * carry that this does not. The schema is `.strict()` so a field cannot be added to the file
 * without somebody deciding to add it here first, which is the check that keeps a reader facing
 * document from quietly growing a column.
 */
export const publishedResultsSchema = z
  .object({
    interaction_id: id('interaction'),
    version: z.number().int().min(1),
    /**
     * Whose numbers these are, when results are scoped per brand.
     *
     * Absent means the combined file, which is every results document written so far. It is here
     * so a per brand file says which masthead it counts rather than being identifiable only by its
     * key, and so a widget that fetched the wrong file can tell.
     */
    brand_id: id('brand').optional(),
    total_responses: z.number().int().min(0),
    counts: z.record(id('option'), z.number().int().min(0)),
  })
  .strict();

export type PublishedResults = z.infer<typeof publishedResultsSchema>;

/**
 * Build the results document, from whichever side is writing it.
 *
 * The projector writes it when an aggregate moves and the API writes an empty one at publish time,
 * and the public endpoint answers with the same fields. One builder means those three cannot drift
 * into three slightly different shapes, and the parse is what stops a caller passing an aggregate
 * straight through with the editor only fields still attached.
 */
export function buildResultsDocument(input: {
  readonly interaction_id: string;
  readonly version: number;
  /** Only for a per brand file. Left out entirely for the combined one, which is the default. */
  readonly brand_id?: string | undefined;
  readonly total_responses?: number | undefined;
  readonly counts?: Record<string, number> | undefined;
}): PublishedResults {
  return publishedResultsSchema.parse({
    interaction_id: input.interaction_id,
    version: input.version,
    ...(input.brand_id === undefined ? {} : { brand_id: input.brand_id }),
    total_responses: input.total_responses ?? 0,
    counts: input.counts ?? {},
  });
}
